// Hunts, pokeidle-style: each hunt is ONE species at a set level, placed in a
// named area on the region map. A small "visitor" chance adds variance: any
// hunt can occasionally turn up a different creature from the same region.
import { CONFIG } from './config.ts';
import { LINES, SPECIES, lineStageAt, type RegionId } from './species.ts';
import type { Rng } from './rng.ts';

export interface AreaDef {
  id: string;
  name: string;
  region: RegionId;
  index: number;
  level: number;
  /** Position on the region map, 0..1 */
  mx: number;
  my: number;
}

export interface GroundDef {
  id: string;
  /** The one species this hunt is for. */
  species: string;
  area: string;
  region: RegionId;
  /** Area index within its region (0-based). */
  index: number;
  /** Hunt level: every wild here is this level, and it unlocks at this trainer level. */
  level: number;
  /** Which stage of the line spawns here (0-based). */
  stage: 0 | 1 | 2;
  /** Same as level; kept for older callers. */
  minLevel: number;
  maxLevel: number;
  bossToken?: string;
  /** Map position, 0..1 */
  mx: number;
  my: number;
}

// Hunt level ladders, one step per area. Midgard climbs 1..180; Hellas opens at trainer level 135 and climbs to 940
// with wider steps; Yomi opens at 760 and climbs to 2,500 with wider steps still. Neighbouring regions overlap (Cole
// 2026-10-20): a region's last hunts sit above the next region's first ones, so a trainer near a border picks between
// two regions (Midgard 150 and 180 against Hellas 135, 165 and 200; Hellas 820 and 940 against Yomi 760 and 880).
// New areas go at the end of a region's list so area ids stay put.
const MIDGARD_LEVELS = [1, 5, 10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90, 100, 100, 110, 125, 150, 180];
const HELLAS_LEVELS = [135, 165, 200, 250, 300, 350, 400, 450, 500, 550, 600, 650, 700, 820, 940];
const YOMI_LEVELS = [760, 880, 1000, 1100, 1200, 1300, 1400, 1500, 1600, 1700, 1800, 1950, 2100, 2300, 2500];

/**
 * Hunts from tokenFrom up to (not including) tokenTo drop the region boss's key. A region's top overlap hunt (Midgard
 * 180, Hellas 940) drops none, so keys stay near the trainer-level band they had before the overlap. With keys on
 * every hunt to the top, grinders farmed Fenrir keys at 180 and found twice as many a week; ending them at 150 (the
 * old next-region base) halved casual players' keys (sim/TUNING_LOG.md, 2026-10-20 regions).
 */
export const REGIONS: { id: RegionId; name: string; mythology: string; base: number; tokenFrom: number; tokenTo?: number }[] = [
  { id: 'midgard', name: 'Midgard', mythology: 'Norse', base: MIDGARD_LEVELS[0], tokenFrom: 90, tokenTo: 180 },
  { id: 'hellas', name: 'Hellas', mythology: 'Greek', base: HELLAS_LEVELS[0], tokenFrom: 700, tokenTo: 940 },
  { id: 'yomi', name: 'Yomi', mythology: 'Japanese', base: YOMI_LEVELS[0], tokenFrom: 2100 },
];

// name, lines found there, map position
const MIDGARD: [string, string[], number, number][] = [
  ['Frostfell Meadow', ['wolf', 'squirrel', 'hare'], 0.18, 0.78],
  ['Rimewood Edge', ['squirrel', 'raven', 'seal', 'owl', 'otter'], 0.3, 0.7],
  ['Mossy Barrows', ['troll', 'draugr', 'tomte', 'lynx'], 0.42, 0.8],
  ['Crag Pass', ['goat', 'troll', 'lynx'], 0.55, 0.68],
  ['Raven Cliffs', ['raven', 'goat', 'valkyrie', 'owl'], 0.7, 0.74],
  ['Fjord Shallows', ['nykk', 'wolf', 'seal', 'fossegrim', 'otter'], 0.84, 0.6],
  ['Golden Fields', ['boar', 'squirrel', 'tomte', 'hare', 'alf'], 0.68, 0.5],
  ['Troll Bridge', ['troll', 'nykk', 'elk', 'bear'], 0.52, 0.48],
  ['Ember Rift', ['ember', 'goat', 'raven', 'dwarf'], 0.36, 0.5],
  ['Barrow Depths', ['draugr', 'raven', 'valkyrie', 'huldra', 'hel'], 0.2, 0.42],
  ['Linnorm Lake', ['wyrm', 'nykk', 'squirrel', 'kraken'], 0.32, 0.28],
  ['Wolfjaw Glacier', ['wolf', 'draugr', 'seal', 'jotun', 'garm'], 0.5, 0.22],
  ['Muspel Gate', ['ember', 'boar', 'tomte', 'dwarf'], 0.68, 0.3],
  ['Yggdrasil Roots', ['squirrel', 'boar', 'wyrm', 'nidhogg', 'hraesvelgr'], 0.82, 0.2],
  ['Gleipnir Hollow', ['wolf', 'wyrm', 'ember', 'sleipnir', 'garm'], 0.6, 0.1],
  // Lv 110-180: each pairs a Common farm with a Rare or Epic that takes more blows.
  ['Ironwood', ['wolf', 'boar', 'nidhogg', 'huldra', 'bear'], 0.42, 0.1],
  ['Bifrost Ridge', ['raven', 'ember', 'valkyrie', 'sleipnir', 'alf', 'hraesvelgr'], 0.25, 0.14],
  ['Nidavellir Mines', ['troll', 'wyrm', 'nidhogg', 'jotun'], 0.09, 0.24],
  ['Hvergelmir Spring', ['squirrel', 'nykk', 'wyrm', 'elk', 'mara'], 0.08, 0.5],
];
// Hellas comes after Midgard's level 100, so its first stages live in its first
// five areas, second stages in the next five, finals in the last five
// (HELLAS_STAGE_SPAN). Each band of five areas holds every Hellas line once, from the band of
// its first form on (a two-stage line starts in the second band, a single creature in the last).
const HELLAS: [string, string[], number, number][] = [
  ['Olive Terraces', ['satyr', 'harpy', 'ram', 'gowl', 'hind'], 0.15, 0.8],
  ['Windy Headland', ['pegasus', 'phoenix', 'harpy', 'griffin', 'bee'], 0.28, 0.66],
  ['Bronze Foundry', ['talos', 'chimera', 'myrmidon', 'centaur'], 0.42, 0.78],
  ['Lerna Marsh', ['hydra', 'siren', 'satyr', 'dolphin', 'hippocamp'], 0.56, 0.84],
  ['Labyrinth Gate', ['minotaur', 'cerberus', 'stymph'], 0.7, 0.72],
  ['Siren Rocks', ['siren', 'harpy', 'gorgon', 'dolphin', 'crab', 'hippocamp'], 0.86, 0.62],
  ['Chimera Crags', ['chimera', 'minotaur', 'cyclops', 'griffin', 'centaur'], 0.72, 0.5],
  ['Asphodel Edge', ['cerberus', 'satyr', 'myrmidon', 'scylla', 'bee', 'ladon'], 0.55, 0.55],
  ['Helicon Slopes', ['pegasus', 'talos', 'ram', 'gowl', 'nymph', 'hind', 'sphinx'], 0.38, 0.5],
  ['Ashen Peak', ['phoenix', 'hydra', 'stymph', 'hippalectryon'], 0.2, 0.4],
  ['Deep Labyrinth', ['minotaur', 'cerberus', 'nemeanlion', 'myrmidon', 'nymph', 'bee'], 0.32, 0.26],
  ['Talos Shore', ['talos', 'siren', 'cyclops', 'crab', 'dolphin', 'hippocamp'], 0.5, 0.3],
  ['Styx Ferry', ['hydra', 'satyr', 'empusa', 'lamia', 'scylla', 'sphinx'], 0.66, 0.24],
  ['Cloud Stables', ['pegasus', 'harpy', 'ram', 'griffin', 'gowl', 'hind', 'centaur'], 0.82, 0.32],
  ['Typhon Rift', ['phoenix', 'chimera', 'hydra', 'gorgon', 'stymph', 'hippalectryon', 'ladon'], 0.62, 0.08],
];
// Yomi follows the same bands as Hellas: first forms in areas 1-5, second
// forms in 6-10, finals in 11-15, each band holding every Yomi line once.
const YOMI: [string, string[], number, number][] = [
  ['Bamboo Thicket', ['kodama', 'tanuki', 'lantern', 'mujina'], 0.14, 0.82],
  ['Rainy Shrine Steps', ['parasol', 'fox', 'komainu', 'usagi'], 0.28, 0.72],
  ['Cedar Ridge', ['tengu', 'baku', 'jorogumo', 'kamaitachi', 'shachi'], 0.44, 0.82],
  ['Reed River Bend', ['kappa', 'ryu', 'namazu', 'koi'], 0.6, 0.76],
  ['Lantern Graveyard', ['yurei', 'oni', 'nekomata'], 0.78, 0.84],
  ['Sake Village', ['tanuki', 'parasol', 'nekomata', 'lantern', 'mujina', 'amabie'], 0.86, 0.62],
  ['Waterfall Pool', ['kappa', 'kodama', 'jorogumo', 'namazu', 'koi', 'shachi'], 0.7, 0.54],
  ['Foxfire Fields', ['fox', 'yurei', 'komainu', 'tsuchinoko', 'usagi'], 0.52, 0.58],
  ['Thunder Peak', ['tengu', 'ryu', 'kamaitachi', 'kirin', 'momen'], 0.34, 0.5],
  ['Dream Temple', ['baku', 'oni', 'nue', 'nurikabe', 'ushioni'], 0.16, 0.44],
  ['Willow Moat', ['yurei', 'kappa', 'gashadokuro', 'umibozu', 'lantern', 'koi', 'shachi'], 0.26, 0.28],
  ['Ogre Gate', ['oni', 'tanuki', 'jorogumo', 'komainu', 'namazu', 'mujina', 'nurikabe'], 0.44, 0.32],
  ['Thousand Gates', ['fox', 'kodama', 'nekomata', 'tsuchinoko', 'rokurokubi', 'usagi'], 0.62, 0.3],
  ['Cloud Summit', ['tengu', 'parasol', 'raiju', 'kamaitachi', 'kirin', 'momen'], 0.82, 0.24],
  ['Yomi Pass', ['ryu', 'baku', 'oni', 'yukionna', 'nue', 'amabie', 'ushioni'], 0.56, 0.08],
];
const HELLAS_STAGE_SPAN = 5;

/** Which stage of a line lives at a level: matches the capture caps (20 / 40 / 100). */
export function stageAt(level: number): 0 | 1 | 2 {
  return level < 20 ? 0 : level < 40 ? 1 : 2;
}

/** Stage that spawns in an area: Midgard follows the capture caps by level; later regions step through the stages by area. */
export function areaStage(region: RegionId, index: number, level: number): 0 | 1 | 2 {
  if (region === 'midgard') return stageAt(level);
  return Math.min(2, Math.floor(index / HELLAS_STAGE_SPAN)) as 0 | 1 | 2;
}

export const AREAS: AreaDef[] = [];
export const GROUNDS: GroundDef[] = [];

function build(region: RegionId, levels: number[], list: typeof MIDGARD, token: string) {
  if (levels.length !== list.length) throw new Error(`${region}: ${levels.length} levels for ${list.length} areas`);
  const { tokenFrom, tokenTo = Infinity } = REGIONS.find((r) => r.id === region)!;
  list.forEach(([name, lines, mx, my], i) => {
    const level = levels[i];
    const area: AreaDef = { id: `${region}-${i + 1}`, name, region, index: i, level, mx, my };
    AREAS.push(area);
    lines.forEach((lineId, j) => {
      const line = LINES.find((l) => l.id === lineId)!;
      const stage = areaStage(region, i, level);
      const sp = lineStageAt(line, stage).id;
      // Spread an area's hunts in a small ring around the area point.
      const ang = (j / lines.length) * Math.PI * 2 + i;
      GROUNDS.push({
        id: `${area.id}-${sp}`, species: sp, area: area.id, region, index: i,
        level, stage, minLevel: level, maxLevel: level,
        bossToken: level >= tokenFrom && level < tokenTo ? token : undefined,
        mx: mx + Math.cos(ang) * 0.035, my: my + Math.sin(ang) * 0.05,
      });
    });
  });
}
build('midgard', MIDGARD_LEVELS, MIDGARD, 'fenrir-token');
build('hellas', HELLAS_LEVELS, HELLAS, 'typhon-token');
build('yomi', YOMI_LEVELS, YOMI, 'hachimata-token');

/** Lowest and highest hunt level that drops a region's boss key, for the key texts. */
export function keyHuntLevels(region: RegionId): { lo: number; hi: number } | null {
  const lv = GROUNDS.filter((g) => g.region === region && g.bossToken).map((g) => g.level);
  return lv.length ? { lo: Math.min(...lv), hi: Math.max(...lv) } : null;
}

export const GROUND: Record<string, GroundDef> = Object.fromEntries(GROUNDS.map((g) => [g.id, g]));
export const AREA: Record<string, AreaDef> = Object.fromEntries(AREAS.map((a) => [a.id, a]));

export function groundUnlocked(g: GroundDef, trainerLevel: number): boolean {
  return trainerLevel >= g.level;
}

/** A region's hunts from this area index on drop its star item (server/hunt.ts defeat(): `g.index >= 9`). */
export const STAR_HUNT_INDEX = 9;
/** Trainer level that opens a region's first star-item hunt: Midgard 60, Hellas 550, Yomi 1,700. */
export function starHuntLevel(region: RegionId): number {
  return Math.min(...GROUNDS.filter((g) => g.region === region && g.index >= STAR_HUNT_INDEX).map((g) => g.level));
}

/** Creatures that can visit a hunt: same region, unlocked by level, any rarity; a short line visits from its first form's stage on. */
function visitorsFor(g: GroundDef): string[] {
  return LINES.filter((l) => l.region === g.region && g.stage >= 3 - l.stages.length).map((l) => lineStageAt(l, g.stage).id).filter((id) => id !== g.species);
}
const VISITORS: Record<string, string[]> = {};

export function rollSpawn(g: GroundDef, rng: Rng): { speciesId: string; level: number; visitor: boolean } {
  const level = g.level;
  if (rng.oneIn(CONFIG.visitorOneIn)) {
    const pool = (VISITORS[g.id] ??= visitorsFor(g));
    const pick = rng.weighted(pool.map((id) => [id, CONFIG.spawnWeight[SPECIES[id].rarity]] as const));
    return { speciesId: pick, level, visitor: true };
  }
  return { speciesId: g.species, level, visitor: false };
}

/** Lowest hunt level at which a species is the main spawn (undefined if it never is). */
export function firstHuntLevel(speciesId: string): number | undefined {
  let best: number | undefined;
  for (const g of GROUNDS) if (g.species === speciesId && (best === undefined || g.level < best)) best = g.level;
  return best;
}

/**
 * Where to fall back after repeated wipes: the highest unlocked hunt of a lower
 * level in the same region, or undefined if there is none.
 */
export function retreatGround(from: GroundDef, trainerLevel: number): GroundDef | undefined {
  let best: GroundDef | undefined;
  for (const g of GROUNDS) {
    if (g.region !== from.region || g.level >= from.level || !groundUnlocked(g, trainerLevel)) continue;
    if (!best || g.level > best.level) best = g;
  }
  return best;
}
